
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { KaiArchetype, KaiColor } from '../types';

type KaiMood = 'idle' | 'listening' | 'thinking' | 'talking' | 'excited' | 'celebrating' | 'sleeping' | 'protective';
export type KaiReaction = 'nod' | 'shake' | 'bounce' | 'pulse' | null;

export type AvatarPlacement = 'above' | 'center';

interface MoveOptions {
    duration?: number;
    size?: number;
    placement?: AvatarPlacement;
}

interface KaiState {
  isEnabled: boolean;
  mood: KaiMood;
  reaction: KaiReaction; // Transient animation state
  targetId: string | null; // DOM ID for flight destination
  message: string | null; // Optional tooltip text
  customSize: number | null; // Override default size
  placement: AvatarPlacement; // Where to sit relative to target
  
  // Customization
  archetype: KaiArchetype;
  color: KaiColor;
}

interface KaiPresenceContextType {
  state: KaiState;
  setIsEnabled: (enabled: boolean) => void;
  setMood: (mood: KaiMood) => void;
  triggerReaction: (reaction: KaiReaction) => void;
  moveTo: (elementId: string | null, options?: MoveOptions) => void;
  speak: (text: string) => void;
  setArchetype: (archetype: KaiArchetype) => void;
  setColor: (color: KaiColor) => void;
  reset: () => void;
}

const KaiPresenceContext = createContext<KaiPresenceContextType | null>(null);

export const useKai = () => {
  const context = useContext(KaiPresenceContext);
  if (!context) {
    throw new Error('useKai must be used within a KaiPresenceProvider');
  }
  return context;
};

export const KaiPresenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<KaiState>({
    isEnabled: true, // Default On
    mood: 'idle',
    reaction: null,
    targetId: null,
    message: null,
    customSize: null,
    placement: 'above',
    archetype: 'sage',
    color: 'indigo'
  });

  // Load basic preference from local storage on mount (Syncs with DB later)
  useEffect(() => {
    try {
        const savedEnabled = localStorage.getItem('kai_avatar_enabled');
        const savedArchetype = localStorage.getItem('kai_archetype');
        const savedColor = localStorage.getItem('kai_color');

        setState(prev => ({ 
            ...prev, 
            isEnabled: savedEnabled !== null ? JSON.parse(savedEnabled) : true,
            archetype: (savedArchetype as KaiArchetype) || 'sage',
            color: (savedColor as KaiColor) || 'indigo'
        }));
    } catch(e) {}
  }, []);

  const setIsEnabled = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, isEnabled: enabled }));
    localStorage.setItem('kai_avatar_enabled', JSON.stringify(enabled));
  }, []);

  const setArchetype = useCallback((archetype: KaiArchetype) => {
      setState(prev => ({ ...prev, archetype }));
      localStorage.setItem('kai_archetype', archetype);
  }, []);

  const setColor = useCallback((color: KaiColor) => {
      setState(prev => ({ ...prev, color }));
      localStorage.setItem('kai_color', color);
  }, []);

  const setMood = useCallback((mood: KaiMood) => {
    setState(prev => ({ ...prev, mood }));
  }, []);

  const triggerReaction = useCallback((reaction: KaiReaction) => {
      setState(prev => ({ ...prev, reaction }));
      // Auto-clear reaction after animation duration (approx 600ms)
      setTimeout(() => {
          setState(prev => ({ ...prev, reaction: null }));
      }, 700);
  }, []);

  const moveTo = useCallback((targetId: string | null, options: MoveOptions = {}) => {
    const { duration = 2000, size = null, placement = 'above' } = options;
    
    setState(prev => ({ 
        ...prev, 
        targetId, 
        customSize: size,
        placement 
    }));
    
    // Auto-reset if duration provided and not permanent (0)
    if (duration > 0 && targetId !== null) {
        setTimeout(() => {
            setState(prev => ({ 
                ...prev, 
                targetId: null, 
                mood: 'idle', 
                customSize: null,
                placement: 'above'
            }));
        }, duration);
    }
  }, []);

  const speak = useCallback((text: string) => {
      setState(prev => ({ ...prev, message: text, mood: 'talking' }));
      setTimeout(() => {
          setState(prev => ({ ...prev, message: null, mood: 'idle' }));
      }, 3000);
  }, []);

  const reset = useCallback(() => {
    setState(prev => ({ 
        ...prev, 
        mood: 'idle', 
        targetId: null, 
        message: null,
        customSize: null,
        placement: 'above'
    }));
  }, []);

  return (
    <KaiPresenceContext.Provider value={{ state, setIsEnabled, setMood, triggerReaction, moveTo, speak, setArchetype, setColor, reset }}>
      {children}
    </KaiPresenceContext.Provider>
  );
};
